import { Component, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { HttpClient } from '@angular/common/http';
import { environment } from '../../environments/environment';

interface AvailabilitySlot {
  date: string;
  start_time: string;
  end_time: string;
  is_available: boolean;
}

interface MeetingEvent {
  _id: string;
  name: string;
  description?: string;
  duration: number;
  timezone: string;
  location_type: string;
  location?: string;
  available_slots: AvailabilitySlot[];
  user_id: {
    name: string;
    email: string;
  };
  candidate_info?: {
    name?: string;
    email?: string;
    phone?: string;
  };
}

@Component({
  selector: 'app-candidate-booking',
  standalone: true,
  imports: [CommonModule, FormsModule],
  templateUrl: './candidate-booking.component.html',
  styleUrls: ['./candidate-booking.component.css']
})
export class CandidateBookingComponent implements OnInit {
  token: string = '';
  meetingEvent: MeetingEvent | null = null;
  loading: boolean = true;
  error: string = '';
  
  // Time selection state
  selectedDate: Date | null = null;
  selectedTimeSlot: AvailabilitySlot | null = null;
  availableDates: Date[] = [];
  availableTimesForDate: AvailabilitySlot[] = [];
  
  // Booking form state
  showBookingForm: boolean = false;
  candidateName: string = '';
  candidateEmail: string = '';
  candidatePhone: string = '';
  additionalNotes: string = '';
  emailError: string = '';
  
  // UI state
  selectedTimezone: string = 'Europe/London';
  currentMonth: Date = new Date();
  calendarDays: any[] = [];
  isSubmitting: boolean = false;
  bookingSuccess: boolean = false;
  
  // UK timezone only (as requested)
  timezoneOptions = [
    { label: 'UK Time (GMT/BST)', value: 'Europe/London' }
  ];
  
  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private http: HttpClient
  ) {}

  // Helper method to format date as YYYY-MM-DD without timezone issues
  private formatDateString(date: Date): string {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  }
  
  async ngOnInit(): Promise<void> {
    console.log('CandidateBookingComponent initialized');
    this.token = this.route.snapshot.params['token'];
    console.log('Candidate booking token:', this.token);
    
    if (!this.token) {
      this.error = 'Invalid booking link';
      this.loading = false;
      return;
    }
    
    await this.loadMeetingEvent();
  }
  
  async loadMeetingEvent(): Promise<void> {
    try {
      const response = await this.http.get<any>(`${environment.apiUrl}/api/candidate/booking/${this.token}`).toPromise();
      
      if (response.success) {
        this.meetingEvent = response.data;
        this.initializeCalendar();
        
        // Pre-fill candidate info if available
        if (this.meetingEvent?.candidate_info) {
          this.candidateName = this.meetingEvent.candidate_info.name || '';
          this.candidateEmail = this.meetingEvent.candidate_info.email || '';
          this.candidatePhone = this.meetingEvent.candidate_info.phone || '';
        }
      } else {
        this.error = response.message || 'Failed to load meeting details';
      }
    } catch (error: any) {
      console.error('Error loading meeting event:', error);
      if (error.status === 404) {
        this.error = 'This booking link has expired or is no longer valid';
      } else {
        this.error = 'Failed to load meeting details. Please try again.';
      }
    } finally {
      this.loading = false;
    }
  }
  
  initializeCalendar(): void {
    if (!this.meetingEvent) return;
    
    // Get unique dates from available slots
    this.availableDates = this.getUniqueDates(this.meetingEvent.available_slots);
    
    // Set initial selected date to first available date
    if (this.availableDates.length > 0) {
      this.selectedDate = this.availableDates[0];
      this.currentMonth = new Date(this.selectedDate);
      this.onDateClick(this.selectedDate);
    }
    
    this.generateCalendarDays();
  }
  
  getUniqueDates(slots: AvailabilitySlot[]): Date[] {
    const dateSet = new Set<string>();
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    
    slots.forEach(slot => {
      if (slot.is_available) {
        const slotDate = new Date(slot.date);
        if (slotDate >= today) {
          dateSet.add(slot.date);
        }
      }
    });
    
    return Array.from(dateSet)
      .map(dateStr => new Date(dateStr))
      .sort((a, b) => a.getTime() - b.getTime());
  }
  
  generateCalendarDays(): void {
    const year = this.currentMonth.getFullYear();
    const month = this.currentMonth.getMonth();
    
    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);
    const startDate = new Date(firstDay);
    startDate.setDate(startDate.getDate() - firstDay.getDay());
    
    const days = [];
    for (let i = 0; i < 42; i++) {
      const date = new Date(startDate);
      date.setDate(startDate.getDate() + i);
      
      days.push({
        date: new Date(date),
        isCurrentMonth: date.getMonth() === month,
        isToday: this.isToday(date),
        isAvailable: this.isDateAvailable(date),
        isSelected: this.isDateSelected(date)
      });
    }
    
    this.calendarDays = days;
  }
  
  isToday(date: Date): boolean {
    const today = new Date();
    return date.toDateString() === today.toDateString();
  }
  
  isDateAvailable(date: Date): boolean {
    const dateStr = this.formatDateString(date);
    return this.availableDates.some(availableDate => 
      this.formatDateString(availableDate) === dateStr
    );
  }
  
  isDateSelected(date: Date): boolean {
    return !!this.selectedDate && date.toDateString() === this.selectedDate.toDateString();
  }
  
  onDateClick(date: Date): void {
    if (!this.isDateAvailable(date)) return;
    
    this.selectedDate = date;
    this.selectedTimeSlot = null; // Reset selected time slot
    this.availableTimesForDate = this.getAvailableTimesForDate(date);
    this.generateCalendarDays(); // Refresh calendar display
  }
  
  getAvailableTimesForDate(date: Date): AvailabilitySlot[] {
    if (!this.meetingEvent) return [];
    
    const dateStr = this.formatDateString(date);
    return this.meetingEvent.available_slots
      .filter(slot => slot.date === dateStr && slot.is_available)
      .sort((a, b) => a.start_time.localeCompare(b.start_time));
  }
  
  onTimeSlotClick(slot: AvailabilitySlot): void {
    this.selectedTimeSlot = slot;
  }
  
  continueBooking(): void {
    if (this.selectedTimeSlot) {
      this.showBookingForm = true;
    }
  }
  
  goBackToTimeSelection(): void {
    this.showBookingForm = false;
  }

  validateEmail(): void {
    if (!this.candidateEmail) {
      this.emailError = '';
      return;
    }
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    this.emailError = emailRegex.test(this.candidateEmail) ? '' : 'Please enter a valid email address';
  }

  async scheduleMeeting(): Promise<void> {
    if (!this.selectedTimeSlot || !this.candidateName || !this.candidateEmail) {
      return;
    }

    // Validate email format
    this.validateEmail();
    if (this.emailError) {
      return;
    }
    
    this.isSubmitting = true;
    
    try {
      const bookingData = {
        candidate_name: this.candidateName,
        candidate_email: this.candidateEmail,
        candidate_phone: this.candidatePhone,
        additional_notes: this.additionalNotes,
        selected_slot: {
          date: this.selectedTimeSlot.date,
          start_time: this.selectedTimeSlot.start_time,
          end_time: this.selectedTimeSlot.end_time
        }
      };
      
      const response = await this.http.post<any>(
        `${environment.apiUrl}/api/candidate/booking/${this.token}/book`,
        bookingData
      ).toPromise();
      
      if (response.success) {
        this.bookingSuccess = true;
      } else {
        alert(response.message || 'Failed to book meeting. Please try again.');
      }
    } catch (error: any) {
      console.error('Error booking meeting:', error);
      
      if (error.status === 409) {
        alert('This time slot is no longer available. Please select another time.');
        // Refresh the meeting event to get updated slots
        await this.loadMeetingEvent();
        this.showBookingForm = false;
      } else if (error.status === 404) {
        alert('This booking link has expired. Please contact the meeting organizer for a new link.');
      } else {
        alert('Failed to book meeting. Please try again.');
      }
    } finally {
      this.isSubmitting = false;
    }
  }
  
  previousMonth(): void {
    this.currentMonth.setMonth(this.currentMonth.getMonth() - 1);
    this.currentMonth = new Date(this.currentMonth);
    this.generateCalendarDays();
  }
  
  nextMonth(): void {
    this.currentMonth.setMonth(this.currentMonth.getMonth() + 1);
    this.currentMonth = new Date(this.currentMonth);
    this.generateCalendarDays();
  }
  
  formatTime(time: string): string {
    const [hours, minutes] = time.split(':');
    const hour = parseInt(hours);
    const ampm = hour >= 12 ? 'PM' : 'AM';
    const hour12 = hour % 12 || 12;
    return `${hour12}:${minutes} ${ampm}`;
  }
  
  getLocationTypeLabel(): string {
    if (!this.meetingEvent) return '';
    
    switch (this.meetingEvent.location_type) {
      case 'in-person':
        return 'In-person meeting';
      case 'zoom':
        return 'Video Call';
      case 'phone':
        return 'Phone Call';
      default:
        return 'Meeting';
    }
  }
  
  getLocationLabel(): string {
    if (!this.meetingEvent) return '';
    
    if (this.meetingEvent.location) {
      return this.meetingEvent.location;
    }
    
    switch (this.meetingEvent.location_type) {
      case 'in-person':
        return 'Location to be confirmed';
      case 'zoom':
        return 'Zoom meeting link will be provided';
      case 'phone':
        return 'Phone number will be provided';
      default:
        return 'Details will be provided';
    }
  }
}